// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the track once for a gapless loop and animates with the Web Animations API.
// Respects prefers-reduced-motion (no animation). Pause sources (hover, focus, hidden tab,
// explicit pause) are tracked independently; an explicit pause via the `--toggle` command is sticky.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #pause = { hover: false, focus: false, hidden: false, explicit: false };

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal });

    this.addEventListener("pointerenter", () => this.#set("hover", true), { signal });
    this.addEventListener("pointerleave", () => this.#set("hover", false), { signal });
    this.addEventListener("focusin", () => this.#set("focus", true), { signal });
    this.addEventListener("focusout", (event) => {
      if (!this.contains(event.relatedTarget)) this.#set("focus", false);
    }, { signal });
    document.addEventListener("visibilitychange", () => this.#set("hidden", document.hidden), { signal });
    this.addEventListener("command", (event) => {
      if (event.command !== "--toggle") return;
      this.#set("explicit", !this.#pause.explicit);
      this.#syncToggle(event.source);
    }, { signal });

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#animation?.cancel();
  }

  #set(key, value) {
    this.#pause[key] = value;
    this.#apply();
  }

  #apply() {
    if (!this.#animation) return;
    const paused = Object.values(this.#pause).some(Boolean);
    if (paused) this.#animation.pause();
    else this.#animation.play();
  }

  #syncToggle(button) {
    if (!button) return;
    const paused = this.#pause.explicit;
    button.setAttribute("aria-pressed", String(paused));
    const label = paused ? button.dataset.playLabel : button.dataset.pauseLabel;
    if (label) button.setAttribute("aria-label", label);
    button.querySelector("[data-icon-pause]")?.toggleAttribute("hidden", paused);
    button.querySelector("[data-icon-play]")?.toggleAttribute("hidden", !paused);
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    if (!track || this.#mql.matches) return;

    // Ensure exactly two copies of the content so the loop is seamless.
    if (!track.dataset.cloned) {
      const clones = [...track.children].map((n) => {
        const clone = n.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        return clone;
      });
      track.append(...clones);
      track.dataset.cloned = "true";
    }

    const distance = track.scrollWidth / 2;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
    this.#apply();
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
